const billingFlagValue = (value, fallback = null) => {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'string') {
        const normalized = value.trim().toLowerCase();
        if (normalized === '0' || normalized === 'false') return false;
        if (normalized === '1' || normalized === 'true') return true;
    }
    if (value === 0 || value === false) return false;
    if (value === 1 || value === true) return true;

    // Historical rows can contain values that predate the current flag
    // contract. Keep those values byte-for-byte for optimistic locking. The
    // editor projects them to a safe boolean without replacing them until the
    // user actually changes the toggle.
    return value;
};

const optionalBillingFlagValue = (value) => value === undefined ? undefined : billingFlagValue(value);

export const getBillingEntryFlagEditorValue = (value, fallback = false) => {
    const normalized = billingFlagValue(value, fallback);
    return typeof normalized === 'boolean' ? normalized : fallback;
};

const billingMutableFields = [
    'billing_rate_id',
    'user_id',
    'date',
    'rate',
    'raw_quantity',
    'final_quantity',
    'final_total',
    'description',
    'draft',
    'billable',
    'chargeable',
    'item_type',
    'entry_kind',
    'flat_fee_code',
    'flat_fee_code_2',
    'unit',
];

const nullableNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
};

const normalizedBillingKind = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (value === 'flatfee' || value === 'flat') return 'flat_fee';
    return value;
};

const billingEntryComparableFields = (entry = {}) => {
    const quantitySource = entry.hours ?? entry.raw_quantity ?? entry.quantity;
    const flatFeeCode2 = Object.prototype.hasOwnProperty.call(entry, 'flat_fee_code_2')
        ? entry.flat_fee_code_2
        : entry.flat_fee_code2;

    return {
        billing_rate_id: nullableNumber(entry.billing_rate_id),
        user_id: nullableNumber(entry.user_id),
        date: entry.date ? String(entry.date).split(/[ T]/)[0] : null,
        rate: nullableNumber(entry.rate),
        raw_quantity: nullableNumber(quantitySource),
        final_quantity: nullableNumber(entry.final_quantity),
        final_total: nullableNumber(entry.final_total),
        description: entry.description ?? null,
        draft: optionalBillingFlagValue(entry.draft),
        billable: optionalBillingFlagValue(entry.billable),
        chargeable: optionalBillingFlagValue(entry.chargeable),
        item_type: normalizedBillingKind(entry.item_type),
        entry_kind: normalizedBillingKind(entry.entry_kind),
        flat_fee_code: entry.flat_fee_code ?? null,
        flat_fee_code_2: flatFeeCode2 ?? null,
        unit: entry.unit ?? null,
    };
};

export const buildBillingEntryEditData = (entry = {}, caseId) => {
    const comparable = billingEntryComparableFields(entry);
    const quantity = comparable.raw_quantity ?? 0;

    return {
        ...entry,
        ...comparable,
        id: entry.id ?? null,
        // Keep legacy NULL storage values in originalEntry for stale-write
        // checks, while giving the controlled date input a safe string value.
        date: comparable.date ?? '',
        hours: quantity,
        raw_quantity: quantity,
        quantity,
        case_id: entry.case_id ?? caseId,
    };
};

export const buildBillingEntryUpdatePayload = (entry = {}, originalEntry = null) => {
    const current = billingEntryComparableFields(entry);

    if (originalEntry) {
        const original = billingEntryComparableFields(originalEntry);
        const payload = { id: entry.id, case_id: entry.case_id };
        const expected = {};

        billingMutableFields.forEach((field) => {
            if (Object.is(current[field], original[field])) return;
            payload[field] = current[field];
            expected[field] = original[field];
        });
        payload.expected = expected;
        return payload;
    }

    const payload = { ...entry, ...current };
    if (current.raw_quantity !== null) {
        payload.quantity = current.raw_quantity;
        payload.hours = current.raw_quantity;
    }
    delete payload.flat_fee_code2;
    billingMutableFields.forEach((field) => {
        if (payload[field] === undefined || payload[field] === null) delete payload[field];
    });
    return payload;
};

export const readBillingEntryMutationResponse = async (response, fallbackMessage) => {
    let data = null;
    try {
        data = await response.json();
    } catch (error) {
        data = null;
    }

    if (!response.ok || !data?.success) {
        throw new Error(data?.message || `${fallbackMessage}${response.status ? ` (${response.status})` : ''}.`);
    }

    return data;
};
